import { useTranslation } from "next-i18next";
import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { HistoryView, PtbSupportedNetwork, getCaveIds } from "@looksrare/poke-the-bear";
import { usePtbPageViews } from "@looksrare/poke-the-bear/hooks/useActiveViews";
import { getCanonicalAndLanguageAlternates } from "@/utils/seo";
import { Page } from "@/components";
import { headers } from "@/server";
import { YoloGamesPtbProvider } from "@/components/YoloGamesPtbProvider";
import { PTBPageContent } from "@/components/Layout/PTBPageContent";
import { getChainInfo } from "@/utils";

interface PTBHistoryProps {
  serverSideCaveOptions?: {
    id: string;
    name: string;
  }[];
}

// @todo-ptbl2 Set up redirect for /poke-the-bear/history to /poke-the-bear/history/ethereum. find any links and udpate, too
const PTBHistory = ({ serverSideCaveOptions }: PTBHistoryProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentChainInfo = getChainInfo();

  // Ensure that the page view is tracked
  usePtbPageViews();

  const title = "Poke the Bear | Past Rounds";
  const description = "See the winners of previous PTB rounds, and verify the source of randomness used.";
  const { canonical, languageAlternates } = getCanonicalAndLanguageAlternates(
    "/poke-the-bear/history",
    router,
    currentChainInfo.appUrl
  );

  return (
    <Page title={t("Poke the Bear")} breadcrumbHref="/poke-the-bear" contentContainerProps={{ pb: "60px" }}>
      <NextSeo
        noindex
        nofollow
        title={title}
        description={description}
        openGraph={{
          url: canonical,
          title,
          description,
          images: [
            {
              url: `${currentChainInfo.appUrl}/images/poke-the-bear/meta.png`,
              alt: "Poke The Bear",
              width: 1600,
              height: 840,
              type: "image/png",
            },
          ],
        }}
        canonical={canonical}
        languageAlternates={languageAlternates}
      />
      <PTBPageContent>
        <YoloGamesPtbProvider>
          <HistoryView serverSideCaveOptions={serverSideCaveOptions} />
        </YoloGamesPtbProvider>
      </PTBPageContent>
    </Page>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, locale = "en" }) => {
  const network = params!.network as PtbSupportedNetwork;
  const caves = await getCaveIds(network, headers);

  if (!caves) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ["ptb", "common"])),
      serverSideCaveOptions: caves.map(({ onChainId, name }) => ({ id: onChainId, name })),
    },
  };
};

export default PTBHistory;
